(() => {
  class CartItems extends HTMLElement {
    constructor() {
      super();
      this.onQuantityChange = window.debounce((event) => {
        const input = event.target.closest('input[data-index]');
        if (input) this.updateQuantity(input.dataset.index, input.value, document.activeElement?.name, input.dataset.quantityVariantId);
      }, 300);
      this.addEventListener('change', (event) => {
        // Fired on blur — e.g. the customer tabs/clicks straight to "Check out"
        // right after typing. Skipping the debounce here (unlike quantity,
        // which only ever changes via repeated spinner clicks) means the save
        // request starts firing immediately instead of ~300ms later, on top of
        // this request's own `keepalive: true` (see updateNote) — both narrow
        // the race between this async save and the checkout form's near-
        // instant navigation away from the page.
        const noteField = event.target.closest('textarea[data-cart-note-index]');
        if (noteField) {
          this.updateNote(noteField);
          return;
        }
        this.onQuantityChange(event);
      });
    }

    getSectionsToRender() {
      const items = document.getElementById('main-cart-items');
      const footer = document.getElementById('main-cart-footer');
      return [
        items && { id: 'main-cart-items', section: items.dataset.id, selector: '.js-contents' },
        footer && { id: 'main-cart-footer', section: footer.dataset.id, selector: '#main-cart-footer' },
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble' },
      ].filter(Boolean);
    }

    updateQuantity(line, quantity, name, variantId) {
      this.enableLoading(line);
      const sections = this.getSectionsToRender();
      fetch(`${window.routes.cart_change_url}.js`, {
        ...window.fetchConfig('javascript'),
        body: JSON.stringify({
          line,
          quantity: Number(quantity),
          sections: sections.map((section) => section.section),
          sections_url: window.location.pathname,
        }),
      })
        .then((response) => response.json())
        .then((state) => {
          if (state.errors) {
            this.updateError(line, state.errors);
            return;
          }

          this.renderSections(state, sections);
          this.classList.toggle('is-empty', state.item_count === 0);
          document.querySelector('cart-drawer')?.classList.toggle('is-empty', state.item_count === 0);
          this.updateLiveRegions(line);
          window.publish?.(window.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state });
        })
        .catch(() => this.updateError(line, window.cartStrings?.error || 'Unable to update the cart.'))
        .finally(() => this.disableLoading(line));
    }

    updateNote(noteField) {
      // Existing (non-Note) properties are embedded server-side and merged back
      // in — /cart/change.js's `properties` param replaces the line's entire
      // property set rather than patching a single key, so omitting the rest
      // would silently wipe any other property already on that line.
      const line = noteField.dataset.cartNoteIndex;
      let properties = {};
      try {
        const parsed = JSON.parse(noteField.dataset.existingProperties || '{}');
        // Liquid's `item.properties | json` renders as `[]`, not `{}`, when a
        // line has zero custom properties (a known Liquid quirk — an empty
        // hash serializes as an empty array). `properties['Note'] = value` on
        // a JS array doesn't throw, but a plain array only keeps numeric-index
        // entries when JSON.stringify'd later — the assigned key silently
        // vanishes from the request body, so the save looks like it worked
        // (no error, request still fires) but the server never receives it.
        properties = Array.isArray(parsed) ? {} : parsed;
      } catch (error) {
        properties = {};
      }
      const key = noteField.dataset.noteKey || 'Note';
      const value = noteField.value.trim();
      if (value) {
        properties[key] = value;
      } else {
        delete properties[key];
      }

      this.enableLoading(line);
      const quantityInput = this.querySelector(`input[data-index="${line}"]`);
      const sections = this.getSectionsToRender();
      fetch(`${window.routes.cart_change_url}.js`, {
        ...window.fetchConfig('javascript'),
        // A note is typically the last thing typed before clicking "Check
        // out", which submits a real HTML form and can navigate away before
        // this async save would otherwise finish — keepalive tells the
        // browser to let the request complete in the background instead of
        // cancelling it on unload, so the note isn't silently lost.
        keepalive: true,
        body: JSON.stringify({
          line,
          quantity: quantityInput ? Number(quantityInput.value) : undefined,
          properties,
          sections: sections.map((section) => section.section),
          sections_url: window.location.pathname,
        }),
      })
        .then((response) => response.json())
        .then((state) => {
          if (state.errors) {
            this.updateError(line, state.errors);
            return;
          }
          this.renderSections(state, sections);
          window.publish?.(window.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state });
        })
        .catch(() => this.updateError(line, window.cartStrings?.error || 'Unable to update the cart.'))
        .finally(() => this.disableLoading(line));
    }

    renderSections(state, sections = this.getSectionsToRender()) {
      sections.forEach((section) => {
        const target = document.getElementById(section.id);
        const html = state.sections?.[section.section];
        if (!target || !html) return;
        target.innerHTML = this.getSectionInnerHTML(html, section.selector);
      });
    }

    getSectionInnerHTML(html, selector) {
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      return selector ? documentFragment.querySelector(selector)?.innerHTML || '' : documentFragment.body.innerHTML;
    }

    updateLiveRegions(line) {
      const message = '';
      const error = document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
      if (error) error.querySelector('.cart-item__error-text').textContent = message;
      const status = document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
      if (status) status.textContent = message;
      const lineStatus = document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');
      if (lineStatus) lineStatus.setAttribute('aria-hidden', 'true');
    }

    updateError(line, message) {
      const error = document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
      if (error) error.querySelector('.cart-item__error-text').textContent = message;
    }

    enableLoading(line) {
      const lineItem = document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
      lineItem?.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.remove('hidden'));
      const status = document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');
      status?.removeAttribute('aria-hidden');
    }

    disableLoading(line) {
      const lineItem = document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
      lineItem?.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
    }
  }

  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      return document.querySelector('cart-drawer')?.getSectionsToRender() || [];
    }
  }

  class CartRemoveButton extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('click', (event) => {
        event.preventDefault();
        this.closest('cart-items, cart-drawer-items')?.updateQuantity(this.dataset.index, 0);
      });
    }
  }

  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.onKeyUp = (event) => (event.code === 'Escape' || event.key === 'Escape') && this.close();
      this.onClick = (event) => {
        if (event.target.closest('#CartDrawer-Overlay, .drawer__close')) this.close();
      };
      this.addEventListener('click', this.onClick);
      document.addEventListener('keyup', this.onKeyUp);
    }

    disconnectedCallback() {
      document.removeEventListener('keyup', this.onKeyUp);
    }

    getSectionsToRender() {
      return [
        { id: 'CartDrawer', section: 'cart-drawer', selector: '#CartDrawer' },
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble' },
      ];
    }

    setActiveElement(element) {
      this.activeElement = element;
    }

    open(activeElement) {
      this.activeElement = activeElement || this.activeElement || document.activeElement;
      this.classList.add('active');
      this.setAttribute('open', '');
      const dialog = this.querySelector('.drawer__inner');
      requestAnimationFrame(() => window.trapFocus(dialog, dialog));
    }

    close() {
      this.classList.remove('active');
      this.removeAttribute('open');
      window.removeTrapFocus(this.activeElement);
    }

    renderContents(state) {
      const sections = this.getSectionsToRender();
      sections.forEach((section) => {
        const target = document.getElementById(section.id);
        const html = state.sections?.[section.section];
        if (!target || !html) return;
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        target.innerHTML = section.selector
          ? documentFragment.querySelector(section.selector)?.innerHTML || ''
          : documentFragment.body.innerHTML;
      });
      this.classList.toggle('is-empty', state.item_count === 0);
      this.open();
    }
  }

  class CartNote extends HTMLElement {
    constructor() {
      super();
      const input = this.querySelector('textarea');
      input?.addEventListener(
        'change',
        window.debounce(() => {
          fetch(`${window.routes.cart_update_url}.js`, {
            ...window.fetchConfig('javascript'),
            body: JSON.stringify({ note: input.value }),
          })
            .then((response) => response.json())
            .then((cartData) => window.publish?.(window.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-note', cartData }));
        },
        300)
      );
    }
  }

  class CartDiscountBlock extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('.cart-discount-form');
      this.input = this.querySelector('input[name="cart-discount-field"]');
      this.messages = {
        empty: this.querySelector('.cart-discount-form__error--empty'),
        invalid: this.querySelector('.cart-discount-form__error--invalid'),
        duplicate: this.querySelector('.cart-discount-form__error--duplicate'),
        success: this.querySelector('.cart-discount-form__success'),
      };
      this.submitButton = this.querySelector('.cart-discount-form__submit');
      this.spinner = this.querySelector('.loading-overlay__spinner');

      this.form?.addEventListener('submit', this.onSubmit.bind(this));
      this.checkPendingAttempt();
    }

    getAppliedCodes() {
      return (this.dataset.appliedCodes || '')
        .split(',')
        .map((code) => code.trim().toLowerCase())
        .filter(Boolean);
    }

    hideMessages() {
      Object.values(this.messages).forEach((el) => {
        if (el) el.style.display = 'none';
      });
    }

    showMessage(key) {
      this.hideMessages();
      const el = this.messages[key];
      if (!el) return;
      el.style.display = 'block';
      if (key === 'success') {
        const duration = Number(this.dataset.successDuration || 3000);
        clearTimeout(this.successTimeoutId);
        this.successTimeoutId = setTimeout(() => {
          el.style.display = 'none';
        }, duration);
      }
    }

    setLoading(loading) {
      this.spinner?.classList.toggle('hidden', !loading);
      if (this.submitButton) this.submitButton.disabled = loading;
    }

    onSubmit(event) {
      event.preventDefault();
      const code = this.input?.value.trim();
      if (!code) {
        this.showMessage('empty');
        return;
      }
      if (this.getAppliedCodes().includes(code.toLowerCase())) {
        this.showMessage('duplicate');
        return;
      }

      this.hideMessages();
      this.setLoading(true);
      // Shopify's /discount/{code} endpoint always redirects to `redirect`
      // whether or not the code is valid — it never returns a machine-readable
      // error. Stash the attempted code and compare cart.discount_applications
      // (re-embedded server-side as data-applied-codes) once we land back here.
      sessionStorage.setItem('cart-discount-attempt', code);
      const redirectTo = window.routes?.cart_url || '/cart';
      window.location.href = `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(redirectTo)}`;
    }

    checkPendingAttempt() {
      const attempted = sessionStorage.getItem('cart-discount-attempt');
      if (!attempted) return;
      sessionStorage.removeItem('cart-discount-attempt');
      this.showMessage(this.getAppliedCodes().includes(attempted.toLowerCase()) ? 'success' : 'invalid');
    }
  }

  if (!customElements.get('cart-items')) customElements.define('cart-items', CartItems);
  if (!customElements.get('cart-drawer-items')) customElements.define('cart-drawer-items', CartDrawerItems);
  if (!customElements.get('cart-remove-button')) customElements.define('cart-remove-button', CartRemoveButton);
  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);
  if (!customElements.get('cart-note')) customElements.define('cart-note', CartNote);
  if (!customElements.get('cart-discount-block')) customElements.define('cart-discount-block', CartDiscountBlock);
})();
